import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

export const PUBLIC_APP_ORIGIN = 'https://app.xprojex.com';

export interface RouteSEOConfig {
  contentKey: string;
  canonicalPath: string;
  noindex: boolean;
}

const PUBLIC_ROUTE_CONFIG: Record<string, Omit<RouteSEOConfig, 'noindex'>> = {
  '/': { contentKey: 'Home', canonicalPath: '/' },
  '/features': { contentKey: 'Features', canonicalPath: '/features' },
  '/tools': { contentKey: 'Features', canonicalPath: '/features' },
  '/how-it-works': { contentKey: 'HowItWorks', canonicalPath: '/how-it-works' },
  '/help-center': { contentKey: 'HelpCenter', canonicalPath: '/help-center' },
  '/help': { contentKey: 'HelpCenter', canonicalPath: '/help-center' },
  '/pricing': { contentKey: 'Pricing', canonicalPath: '/pricing' },
  '/programs': { contentKey: 'Programs', canonicalPath: '/programs' },
  '/faqs': { contentKey: 'Faq', canonicalPath: '/faqs' },
  '/reviews': { contentKey: 'Examples', canonicalPath: '/reviews' },
  '/get-started': { contentKey: 'GetStarted', canonicalPath: '/get-started' },
  '/onboarding': { contentKey: 'GetStarted', canonicalPath: '/get-started' },
  '/concept-validator': { contentKey: 'ConceptValidator', canonicalPath: '/concept-validator' },
  '/demo': { contentKey: 'ConceptValidator', canonicalPath: '/concept-validator' },
  '/impact-evaluator': { contentKey: 'Impact', canonicalPath: '/impact-evaluator' },
  '/wayfinder': { contentKey: 'Wayfinder', canonicalPath: '/wayfinder' },
  '/game-changer': { contentKey: 'GameChanger', canonicalPath: '/game-changer' },
};

const normalizePathname = (pathname: string) => {
  if (!pathname || pathname === '/') return '/';
  return `/${pathname.split('/').filter(Boolean).join('/')}`.toLowerCase();
};

export const resolveRouteSEO = (pathname: string): RouteSEOConfig => {
  const normalizedPath = normalizePathname(pathname);
  const publicConfig = PUBLIC_ROUTE_CONFIG[normalizedPath];

  if (publicConfig) {
    return { ...publicConfig, noindex: false };
  }

  const privateContentKey = normalizedPath === '/admin' || normalizedPath.startsWith('/admin/')
    ? 'AdminPrivate'
    : normalizedPath === '/programs/workspace' || normalizedPath.startsWith('/programs/workspace/')
      ? 'ProgramPrivate'
      : 'Private';

  return {
    contentKey: privateContentKey,
    canonicalPath: '/',
    noindex: true,
  };
};

const RouteSEO: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const config = resolveRouteSEO(pathname);
  const keywords = t('seoKeywords')
    .split(',')
    .map((keyword) => keyword.trim())
    .filter(Boolean);

  useSEO({
    title: t(`seo${config.contentKey}Title`),
    description: t(`seo${config.contentKey}Description`),
    keywords,
    canonical: `${PUBLIC_APP_ORIGIN}${config.canonicalPath}`,
    publicOrigin: PUBLIC_APP_ORIGIN,
    noindex: config.noindex,
  });

  return null;
};

export default RouteSEO;
